import {
  type CollectionReference,
  Firestore,
  ISoftDelete,
  type WithRef,
  firstResult$,
  isEnumValue,
  isObject,
  query,
  query$,
  snapshot,
  where,
} from '@principle-theorem/shared';
import { first } from 'lodash';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export enum IntegrationType {
  HicapsConnect = 'hicapsConnect',
  Medipass = 'medipass',
  Parchment = 'parchment',
  Podium = 'podium',
  Sendgrid = 'sendgrid',
  Slack = 'slack',
  Stripe = 'stripe',
  TNZ = 'tnz',
  Twilio = 'twilio',
  Typesense = 'typesense',
  ViaApi = 'viaApi',
}

export enum IntegrationCapability {
  Claims = 'claims',
  Email = 'email',
  Payments = 'payments',
  Prescriptions = 'prescriptions',
  SMS = 'sms',
}

export const INTEGRATION_CAPABILITY_MAP: Record<
  IntegrationType,
  IntegrationCapability[]
> = {
  [IntegrationType.HicapsConnect]: [
    IntegrationCapability.Claims,
    IntegrationCapability.Payments,
  ],
  [IntegrationType.Medipass]: [
    IntegrationCapability.Claims,
    IntegrationCapability.Payments,
  ],
  [IntegrationType.Parchment]: [IntegrationCapability.Prescriptions],
  [IntegrationType.Podium]: [IntegrationCapability.SMS],
  [IntegrationType.Sendgrid]: [IntegrationCapability.Email],
  [IntegrationType.Slack]: [],
  [IntegrationType.Stripe]: [IntegrationCapability.Payments],
  [IntegrationType.TNZ]: [IntegrationCapability.SMS],
  [IntegrationType.Twilio]: [IntegrationCapability.SMS],
  [IntegrationType.Typesense]: [],
  [IntegrationType.ViaApi]: [IntegrationCapability.SMS],
};

export interface IIntegration<T extends object = object> extends ISoftDelete {
  type: IntegrationType;
  capabilities: IntegrationCapability[];
  data: T;
}

export function isIntegration<T extends object>(
  data: unknown
): data is IIntegration<T> {
  return (
    isObject(data) &&
    isEnumValue(IntegrationType, data.type) &&
    isObject(data.data)
  );
}

export class Integration {
  static async getByCapability<T extends object>(
    integrationsCollection: CollectionReference<IIntegration<T>>,
    capability: IntegrationCapability
  ): Promise<IIntegration<T>[]> {
    const integrations = await query<IIntegration<T>>(
      integrationsCollection,
      where('capabilities', 'array-contains', capability)
    );

    return integrations.filter((result) => !result.deleted);
  }
}

export interface IIntegrationStorage<T extends object> {
  add(
    integrationsCollection: CollectionReference<IIntegration<T>>,
    data: T,
    capabilities: IntegrationCapability[]
  ): Promise<void>;
  remove(
    integrationsCollection: CollectionReference<IIntegration<T>>
  ): Promise<void>;
  get(
    integrationsCollection: CollectionReference<IIntegration<T>>
  ): Promise<IIntegration<T> | undefined>;
  has$(
    integrationsCollection: CollectionReference<IIntegration<T>>
  ): Observable<boolean>;
}

export class IntegrationStorage<T extends object>
  implements IIntegrationStorage<T>
{
  constructor(private _type: IntegrationType) {}

  col(
    integrationCollection: CollectionReference<IIntegration>
  ): CollectionReference<IIntegration<T>> {
    return integrationCollection as CollectionReference<IIntegration<T>>;
  }

  async add(
    integrationsCollection: CollectionReference<IIntegration<T>>,
    data: T
  ): Promise<void> {
    await Firestore.addDoc(integrationsCollection, {
      type: this._type,
      data,
      capabilities: INTEGRATION_CAPABILITY_MAP[this._type],
      deleted: false,
    });
  }

  async get(
    integrationsCollection: CollectionReference<IIntegration<T>>
  ): Promise<WithRef<IIntegration<T>> | undefined> {
    return snapshot(this.get$(integrationsCollection));
  }

  has$(
    integrationsCollection: CollectionReference<IIntegration<T>>
  ): Observable<boolean> {
    return firstResult$(
      integrationsCollection,
      where('type', '==', this._type)
    ).pipe(map((result) => !!result));
  }

  async remove(
    integrationsCollection: CollectionReference<IIntegration<T>>
  ): Promise<void> {
    const integration = await Firestore.firstResult(
      integrationsCollection,
      where('type', '==', this._type)
    );
    if (!integration) {
      return;
    }
    await Firestore.deleteDoc(integration.ref);
  }

  get$(
    integrationsCollection: CollectionReference<IIntegration<T>>
  ): Observable<WithRef<IIntegration<T>> | undefined> {
    return query$<IIntegration<T>>(
      integrationsCollection,
      where('type', '==', this._type)
    ).pipe(
      map((results) => first(results.filter((result) => !result?.deleted)))
    );
  }

  async update(
    integrationsCollection: CollectionReference<IIntegration<T>>,
    data: T
  ): Promise<void> {
    const existing = await this.get(integrationsCollection);
    if (existing) {
      await Firestore.patchDoc(existing.ref, { data });
      return;
    }
  }

  async upsert(
    integrationsCollection: CollectionReference<IIntegration<T>>,
    data: T
  ): Promise<void> {
    const existing = await this.get(integrationsCollection);
    if (existing) {
      await Firestore.patchDoc(existing.ref, { data });
      return;
    }
    await this.add(integrationsCollection, data);
  }
}
